import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { peopleApi, languageToTMDB } from '../services/api';
import { ArrowLeft, User, Film, Calendar, MapPin } from 'lucide-react';

const StarDetailPage = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const [star, setStar] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStar = async () => {
      try {
        setLoading(true);
        const tmdbLang = languageToTMDB[language] || 'en-US';
        const data = await peopleApi.getDetail(parseInt(id), tmdbLang);
        setStar(data);
      } catch (error) {
        console.error('Error fetching star:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStar();
  }, [id, language]);

  if (loading) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen">
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
            <div className="animate-pulse">
              <div className="h-8 bg-[#1a2420] rounded w-32 mb-8" />
              <div className="flex flex-col md:flex-row gap-8">
                <div className="w-64 aspect-[2/3] bg-[#1a2420] rounded-lg" />
                <div className="flex-1 space-y-4">
                  <div className="h-10 bg-[#1a2420] rounded w-64" />
                  <div className="h-6 bg-[#1a2420] rounded w-32" />
                  <div className="h-32 bg-[#1a2420] rounded" />
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!star) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen">
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] text-center">
            <h1 className="text-4xl font-semibold text-[#E6FFF6] mb-4">
              {t('stars.notFound') || 'Star Not Found'}
            </h1>
            <Link to="/stars" className="text-[#52ea88] hover:underline">
              {t('stars.backToStars') || 'Back to Stars'}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#0B0F0E] min-h-screen">
      <Header />

      <main className="pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          {/* Back button */}
          <Link
            to="/stars"
            className="inline-flex items-center gap-2 text-[#52ea88] hover:text-[#E6FFF6] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('stars.backToStars') || 'Back to Stars'}
          </Link>

          {/* Star Profile */}
          <div className="flex flex-col md:flex-row gap-8">
            {/* Profile Image */}
            <div className="md:w-80 flex-shrink-0">
              <div className="aspect-[2/3] rounded-lg overflow-hidden bg-[#1a2420]">
                {star.profile_path ? (
                  <img
                    src={star.profile_path}
                    alt={star.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-24 h-24 text-[#52ea88]/30" />
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-semibold text-[#E6FFF6] mb-2">
                {star.name}
              </h1>
              
              <p className="text-xl text-[#52ea88] mb-6">
                {star.known_for_department}
              </p>

              {/* Details */}
              <div className="flex flex-wrap gap-6 mb-8 text-[#9FBFB4]">
                {star.birthday && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#52ea88]" />
                    <span>{star.birthday}</span>
                  </div>
                )}
                {star.place_of_birth && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#52ea88]" />
                    <span>{star.place_of_birth}</span>
                  </div>
                )}
              </div>

              {/* Biography */}
              {star.biography && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold text-[#E6FFF6] mb-4">
                    {t('stars.biography') || 'Biography'}
                  </h2>
                  <p className="text-[#9FBFB4] leading-relaxed whitespace-pre-line">
                    {star.biography}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Recent/Popular Movies */}
          {star.recent_movies && star.recent_movies.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-semibold text-[#E6FFF6] mb-6 flex items-center gap-3">
                <Film className="w-6 h-6 text-[#52ea88]" />
                {t('stars.recentMovies') || 'Recent & Popular Movies'}
              </h2>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {star.recent_movies.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/movie/${movie.id}`}
                    className="group"
                  >
                    <div className="aspect-[2/3] rounded-lg overflow-hidden bg-[#1a2420]">
                      {movie.poster ? (
                        <img
                          src={movie.poster}
                          alt={movie.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-12 h-12 text-[#52ea88]/30" />
                        </div>
                      )}
                    </div>
                    <div className="mt-3">
                      <h3 className="font-medium text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors line-clamp-2">
                        {movie.title}
                      </h3>
                      {movie.year && (
                        <p className="text-sm text-[#9FBFB4]">{movie.year}</p>
                      )}
                      {movie.character && (
                        <p className="text-xs text-[#52ea88] mt-1">
                          as {movie.character}
                        </p>
                      )}
                      {movie.job && (
                        <p className="text-xs text-[#52ea88] mt-1">
                          {movie.job}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StarDetailPage;
